import { ValidateRule } from '@/types/components';

const PASSWORD_SPECIAL_CHARS = /[!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?~-]/;

const PASSWORD_ALLOWED_CHARS = /^[A-Za-z0-9!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?~-]+$/;

const getValidationError = (rules: ValidateRule[], val: string) => {
  for (const rule of rules) {
    if (!rule.test(val)) {
      return rule.message;
    }
  }
  return '';
};

/**
 * @desc 이름 유효성 검사
 * @param {string} name - 검증할 이름
 * @returns {string} 에러가 있다면 다국어 키 반환
 */
export const validateName = (name: string): string => {
  return getValidationError(
    [
      {
        test: (v: string) => !!v,
        message: 'alert_enter_name',
      },
      {
        test: (v: string) => v.length >= 2 && v.length <= 30,
        message: 'alert_name_length_limit',
      },
    ],
    name
  );
};

/**
 * @desc 아이디 유효성 검사
 * @param {string} id - 검증할 아이디
 * @returns {string} 에러가 있다면 다국어 키 반환
 */
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export const validatePartnerCode = (partnerCode: string): string => {
  return getValidationError([{ test: (v: string) => !!v, message: 'login_select_partner_code' }], partnerCode);
};

export const validateSubUserId = (userId: string): string => {
  return getValidationError([{ test: (v: string) => !!v, message: 'alert_enter_id' }], userId);
};

export const validateSubUserPw = (password: string): string => {
  return getValidationError([{ test: (v: string) => !!v, message: 'alert_enter_password' }], password);
};

export const validateId = (id: string): string => {
  return getValidationError(
    [
      {
        test: (v: string) => !!v,
        message: 'alert_enter_id',
      },
      {
        test: (v: string) => v.length >= 5 && v.length <= 20,
        message: 'alert_user_id_length_limit',
      },
      {
        test: (v: string) => {
          // 이메일 형식 허용
          if (EMAIL_REGEX.test(v)) {
            return true;
          }
          // 숫자만으로 구성된 경우 6자 이상이어야 함
          if (/^\d+$/.test(v)) {
            return v.length >= 6;
          }
          // 영문, 숫자, _, - 조합 허용 (영문이나 숫자로 시작 가능)
          return /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/.test(v);
        },
        message: 'alert_letters_numbers',
      },
    ],
    id
  );
};

/**
 * @desc 비밀번호 유효성 검사
 * @param {string} password - 검증할 비밀번호
 * @returns {string} 에러가 있다면 다국어 키 반환
 */
export const validatePassword = (password: string): string => {
  return getValidationError(
    [
      {
        test: (v: string) => !!v,
        message: 'alert_enter_password',
      },
      {
        test: (v: string) => v.length >= 8 && v.length <= 20,
        message: 'alert_password_length_limit',
      },
      {
        test: (v: string) => {
          const hasLetter = /[A-Za-z]/.test(v);
          const hasNumber = /[0-9]/.test(v);
          const hasSpecial = PASSWORD_SPECIAL_CHARS.test(v);
          const typeCount = [hasLetter, hasNumber, hasSpecial].filter(Boolean).length;

          return PASSWORD_ALLOWED_CHARS.test(v) && typeCount >= 2;
        },
        message: 'alert_combination',
      },
    ],
    password
  );
};

/**
 * @desc 비밀번호 재확인 검사
 * @param {string} password - 원본 비밀번호
 * @param {string} passwordConfirm - 확인용 비밀번호
 * @returns {string} 에러가 있다면 다국어 키 반환
 */
export const validatePasswordConfirm = (password: string, passwordConfirm: string): string => {
  return getValidationError(
    [
      {
        test: (v: string) => v && v.trim() !== '',
        message: 'alert_reenter_password',
      },
      {
        test: (v: string) => v === password,
        message: 'alert_passwords_do_not_match',
      },
    ],
    passwordConfirm
  );
};

/**
 * @desc 언어 유효성 검사
 * @param {string} language - 검증할 언어 코드
 * @returns {string} 에러가 있다면 다국어 키 반환
 */
export const validateLanguage = (language: string): string => {
  return getValidationError(
    [
      {
        test: (v: string) => !!v,
        message: 'alert_select_language',
      },
    ],
    language
  );
};

/**
 * @desc 설명 유효성 검사
 * @param {string} description - 검증할 설명
 * @returns {string} 에러가 있다면 다국어 키 반환
 */
export const validateDescription = (description: string): string => {
  return getValidationError(
    [
      {
        test: (v: string) => v.length <= 100,
        message: 'alert_enter_description_limit',
      },
    ],
    description
  );
};
